<template>
  <div
    class="product-awards-list"
    :class="{
      'product-awards-list--no-awards': filteredAwards.length < 1,
      'product-awards-list--no-filter': vintageOptions.length < 3
    }"
  >
    <div class="product-awards__top product-awards-list__top">
      <div
        v-if="vintageOptions.length >= 3"
        class="product-awards-list__filter"
      >
        <div class="product-awards-list__select">
          <label class="product-awards-list__select-label">
            {{ $t('awards.filterBy') }}
          </label>
          <select-vue
            class="product-awards__select select--formless"
            v-model="selectedFilter"
            name="filter"
            :options="allVintagesOptions"
            :initial-value="selectedFilter"
            ref="vintageSelectRef"
          />
        </div>
      </div>
      <div
        class="product-awards-list__container"
        v-if="reviewsPresentForCurrentVintage"
      >
        <div
          v-for="(award, index) in filteredAwards"
          :key="award.hash"
          class="product-awards-list__tile"
          :class="{'product-awards-list__tile--hidden': (index > 2 && !showAllTiles)}"
        >
          <image-element
            v-if="award.raterImage"
            class="product-awards-list__tile-image"
            :src="[award.raterImage.big]"
            :alt="$t('awards.image.alt', [award.rater]).toString()"
          />
          <image-element
            v-else-if="award.logo && award.logo.big"
            class="product-awards-list__tile-image"
            :src="[award.logo.big]"
            :alt="$t('awards.image.alt', [award.rater]).toString()"
          />
          <div class="product-awards-list__tile-header">
            <div class="product-awards-list__text-rater">
              {{ award.rater }}
            </div>
            <div>
              <span
                v-if="award.rating"
                class="product-awards-list__text-rating"
              >
                {{ formatRatingText(award.rating) }}
              </span>
              <span
                v-if="award.vintage"
                class="product-awards-list__text-vintage"
              >
                {{ $t('awards.vintage', [award.vintage.toString()]) }}
              </span>
            </div>
          </div>
          <div class="product-awards-list__tile-content">
            <div
              v-if="award.detailAwardLongText"
              class="product-awards-list__tile-text detailAwardLongText"
              v-html="award.detailAwardLongText"
            />
            <div
              v-if="award.detailAwardShortText"
              class="product-awards-list__tile-text product-awards-list__tile-text--quote detailAwardShortText"
            >
              <div class="product-awards-list__tile-text--quote-inner">
                <blockquote
                  v-html="formatHTMLtoText(award.detailAwardShortText)"
                />
              </div>
            </div>
            <div
              v-if="award.detailPublisher"
              class="product-awards-list__tile-text detailPublisher"
              v-html="award.detailPublisher"
            />
            <div
              v-if="award.detailPublication !== award.rater"
              class="product-awards-list__text-detail-publication"
              :class="{'product-awards-list__text-detail-publication-margin' : (!award.detailAwardShortText && !award.detailAwardLongText)}"
            >
              <span
                v-html="award.detailPublication"
              />
            </div>
          </div>
          <div
            class="product-awards-list__more-content"
            v-if="award.detailShortRaterText || award.detailShortScaleText"
          >
            <input
              type="checkbox"
              :id="`product-awards-list__more-id-${index}`"
              class="product-awards-list__more-checkbox"
            >
            <div
              v-if="award.detailShortScaleText || (award.raterScaleImage && award.raterScaleImage.big)"
              class="product-awards__item product-awards__item--scala"
            >
              <div
                class="product-awards__item-headline"
                v-html="$t('awards.scale.description')"
              />
              <image-element
                v-if="award.raterScaleImage && award.raterScaleImage.big"
                img-class="product-awards__item-image"
                :src="[award.raterScaleImage.big]"
                :alt="$t('awards.scale.description')"
              />
              <div
                class="product-awards__item-text"
                v-if="award.detailShortScaleText"
                v-html="award.detailShortScaleText"
              />
            </div>
            <div
              v-if="award.rater || award.detailRaterSubHeadline || award.detailShortRaterText || award.raterLink"
              class="product-awards__item product-awards__item--premium"
            >
              <div
                class="product-awards__item-headline"
                v-html="$t('awards.rater.description')"
              />
              <div class="product-awards__item-wrapper">
                <div v-if="award.rater">
                  <div
                    class="product-awards__item-text product-awards__item-text--rater"
                    v-html="award.rater"
                  />
                </div>
                <div v-if="award.detailRaterSubHeadline">
                  <div
                    class="product-awards__item-text product-awards__item-text--detailRaterSubHeadline"
                    v-html="award.detailRaterSubHeadline"
                  />
                </div>
                <div v-if="award.detailShortRaterText">
                  <div
                    class="product-awards__item-text product-awards__item-text--detailShortRaterText"
                    v-html="award.detailShortRaterText"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="award.raterLink"
              class="product-awards-list__link-rater"
            >
              <a
                class="product-awards-list__link-text"
                :href="award.raterLink"
                :title="`${$t('awards.details.button')}: ${award.rater}`"
              >
                {{ $t('awards.link', [award.rater.toString()]) }}
              </a>
            </div>
            <label
              :for="`product-awards-list__more-id-${index}`"
              class="product-awards-list__more-label"
            >
              <span class="product-awards-list__more-button">{{ $t('awards.details.button') }}</span>
              <span
                class="product-awards-list__less-button"
                @click="scrollToContent(index)"
              >
                {{ $t('awards.details.lessButton') }}
              </span>
            </label>
          </div>
          <div
            v-if="award.raterLink && !award.detailShortRaterText && !award.detailShortScaleText"
            class="product-awards-list__more-link-rater"
          >
            <a
              class="product-awards-list__link-text"
              :href="award.raterLink"
              :title="`${$t('awards.details.button')}: ${award.rater}`"
            >
              {{ $t('awards.link', [award.rater.toString()]) }}
            </a>
          </div>
        </div>
        <div
          v-if="filteredAwards.length > 3"
        >
          <button
            class="product-awards-list__button"
            type="button"
            @click="toggleShowAllTiles()"
          >
            <span class="product-awards-list__link-text">
              {{ showAllTiles ? $t('awards.btn.less') : $t('awards.btn.more') }}
            </span>
          </button>
        </div>
      </div>
      <div
        v-else-if="uniqueAwardsVintageList.length > 0"
        class="product-awards__no-awards"
      >
        {{ $t('awards.no_awards_for_vintage_but_others') }}

        <ul class="product-awards__vintage-link-list">
          <li
            v-for="vintage in uniqueAwardsVintageList"
            :key="vintage"
            @click="selectVintage(vintage)"
            class="product-awards__vintage-link"
          >
            {{ vintage }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Vue, Options, Prop, Ref, Watch} from 'vue-property-decorator';
  import pigeon from '../../../bps/utilities/js/pigeon/pigeon';
  import selectVue from '../../../bps/components/select/select.vue';
  import LazyLoad from '../../../bps/components/lazyLoad/lazyLoad.vue';
  import ImageElement from '../../../bps/components/imageElement/imageElement.vue';
  import productStore from '../../../bps/stores/product/ProductStore';
  import type {Award} from '../../../bps/types/Award';
  import type {ProductVariationItem} from '../../../bps/types/ProductVariationItem';
  import scrollToElement from 'scroll-to-element';

  const VINTAGE_IDENTIFIER = 'import:Jahrgang';

  @Options({
    name: 'product-awards-list',
    components: {
      ImageElement,
      LazyLoad,
      selectVue
    }
  })
  export default class ProductAwardsCarousel extends Vue {
    @Ref('vintageSelectRef')
    readonly vintageSelectRef;

    @Prop()
    moodImageSrc: string;

    @Prop({required: true, type: Array})
    awards: Award[];

    @Prop({default: false})
    hasAwardDetails: boolean;

    selectedFilter = null;
    reviewsPresentForCurrentVintage: boolean = false;
    showAllTiles: boolean = false;
    vintageWithoutReviewList = []

    @Watch('selectedFilter')
    onFilterChange(val: string) {
      this.reviewsPresentForCurrentVintage = this.uniqueAwardsVintageList.some(vintage => vintage === val || val === '')
      this.publishAwardCount(this.uniqueAwardsVintageList.filter(vintage => vintage === val).length)
    }

    mounted() {
      this.publishAwardCount(this.awards.length);
      pigeon.subscribe('productPage::variationChange', this.onVariationChange);
    }

    onVariationChange({itemId}: { itemId: string}) {
      const variationItemById = productStore.productVariation.items.find((item) => item.itemId === itemId);
      if (variationItemById) {
        this.resolveReviewsState(variationItemById).then(vintage => {
          this.selectedFilter = vintage;

          if (this.vintageSelectRef) {
            this.vintageSelectRef.setValue(this.selectedFilter);
          }
        });
      }
    }

    resolveReviewsState(variationItemById: ProductVariationItem): Promise<string> {
      const vintageObject = variationItemById.attributes.find(val => val.identifier === VINTAGE_IDENTIFIER)

      if (!vintageObject) {
        if (this.awards && this.uniqueAwardsVintageList.length < 3) {
          return Promise.resolve(this.awards[0].vintage || '');
        }
        return Promise.reject();
      }

      const isVintageAlreadyOnTheList = this.allVintagesOptions.find(item => item.value === vintageObject.value);
      this.reviewsPresentForCurrentVintage = this.uniqueAwardsVintageList.some(review => review === vintageObject.value);

      if (!this.reviewsPresentForCurrentVintage && !isVintageAlreadyOnTheList) {
        const label = this.$t('awards.filter.vintage', [vintageObject.value]).toString();
        this.vintageWithoutReviewList.push({label: label, value: vintageObject.value});
      }

      return Promise.resolve(vintageObject.value);
    }

    publishAwardCount(count: number) {
      pigeon.publish('productAwards:updateCount', count);
    }

    selectVintage(vintage: string) {
      this.vintageSelectRef.setValue(vintage);
    }

    toggleShowAllTiles() {
      this.showAllTiles = !this.showAllTiles;

      if (!this.showAllTiles) {
        scrollToElement(this.$el, {offset: -90, duration: 80});
      }
    }

    scrollToContent(index: number) {
      const award = document.querySelectorAll('.product-awards-list__tile')[index];
      scrollToElement(award, {offset: -90, duration: 80});
    }

    formatRatingText(rating: string): string {
      return rating.replace(' / ', '/');
    }

    formatHTMLtoText(html: string): string {
      return html.replace(/<[^>]*>?/gm, '');
    }

    get uniqueVintageList() {
      const uniqueVintages = [];
      productStore.productVariation.items
        .map(item => item.attributes)
        .map(attribute =>
          attribute.filter(item => {
            if (item.identifier === VINTAGE_IDENTIFIER) {
              uniqueVintages.push(item.value);
            }
          })
        )
      return uniqueVintages;
    }

    get vintageOptions(): {value: string, label: string}[] {
      let vintageOptions = []
      if (this.uniqueVintageList.length) {
        vintageOptions = Array.from(new Set([...this.uniqueVintageList, ...this.uniqueAwardsVintageList])).map((vintage) => {
          const label = this.$t('awards.filter.vintage', [vintage]).toString();
          return {label: label, value: vintage}
        });
      }

      return [
        ...vintageOptions,
        {label: this.$t('awards.filter.all_vintages').toString(), value: ''},
      ]
    }

    get allVintagesOptions(): {value: string, label: string}[] {
      const allVintages = [...this.vintageOptions, ...this.vintageWithoutReviewList];
      return allVintages.sort((a, b) => b.value - a.value)
    }

    get filteredAwards(): Award[] {
      let awards;
      if (this.selectedFilter) {
        awards = this.awards.filter((award) => award.vintage === this.selectedFilter);
      } else {
        awards = this.awards;
      }
      this.publishAwardCount(awards.length);
      return awards;
    }

    get uniqueAwardsVintageList(): string[] {
      return Array.from(new Set(this.awards.map(award => award.vintage)))
    }
  }
</script>
